import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CourseTable from '../../../components/tables/CourseTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCourseApi } from '../../../hooks/useCourseApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmAction, confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  CourseDTO,
  CourseEvaluationDTO,
  CourseTypeDTO,
  CourseStatusDTO,
  PaymentTypeDTO,
  EvaluationStatusDTO,
  EmailMessageDTO,
  NoteDTO,
} from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import Drawer from '../../../components/common/Drawer';
import { CourseTypeConstants } from '../../../constants/CourseTypeConstants';
import { CourseMaterialTypeConstants } from '../../../constants/CourseMaterialTypeConstansts';
import { CourseStatusConstants } from '../../../constants/CourseStatusConstants';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { DrawerViewConstants, CourseDrawerViews } from '../../../constants/DrawerViewConstants';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useNoteApi } from '../../../hooks/useNoteApi';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import ViewInvoiceHistory from '../../../components/drawer/ViewInvoiceHistory';
import { usePaymentApi } from '../../../hooks/usePaymentApi';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { useCourseEvaluationApi } from '../../../hooks/useCourseEvaluationApi';
import { useCourseTypes } from '../../../hooks/useCourseTypeApi';
import { useCourseMaterialTypeApi } from '../../../hooks/useCourseMaterialTypeApi';
import { useCourseStatusApi } from '../../../hooks/useCourseStatusApi';
import { usePaymentTypes } from '../../../hooks/usePaymentTypeApi';
import { useEvaluationStatusApi } from '../../../hooks/useEvaluationStatusApi';
import { getRoles } from '../../../utils/tokenUtils';
import CourseEvaluation from '../../../components/drawer/CourseEvaluation';
import { ChevronDown } from 'lucide-react';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import { FileViewer } from '../../../components/common/FileViewer';
import { useDedicatedStatusApi } from '../../../hooks/useDedicatedStatusApi';

const CourseList: React.FC = () => {
  const { edit, search, remove, extract, restoreFromDeleted, sendProofOfPaymentLink, isLoading, error, updateStatus, getForfeitureDocument } = useCourseApi();
  const { generateCertificate: generateDedicatedCertificate } = useDedicatedStatusApi();
  const { processEftPayment } = usePaymentApi();
  const [items, setItems] = useState<CourseDTO[]>([]);

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('courses', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseDTO>({});
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [drawerView, setDrawerView] = useState<CourseDrawerViews>();
  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [drawerSize, setDrawerSize] = useState<'small' | 'large'>('large');
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const statusFieldName = 'courseStatusId';

  const [courseEvaluation, setCourseEvaluation] = useState<CourseEvaluationDTO[]>([]);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);

  // Add state for dropdown options
  const [courseTypes, setCourseTypes] = useState<CourseTypeDTO[]>([]);
  const [courseMaterialTypes, setCourseMaterialTypes] = useState<CourseTypeDTO[]>([]);
  const [courseStatuses, setCourseStatuses] = useState<CourseStatusDTO[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeDTO[]>([]);
  const [evaluationStatuses, setEvaluationStatuses] = useState<EvaluationStatusDTO[]>([]);

  const { getByCourseId } = useCourseEvaluationApi();
  const { data: courseTypesData = [], isLoading: isLoadingCourseTypes } = useCourseTypes();
  const { search: searchCourseMaterialTypes, isLoading: isLoadingCourseMaterialTypes } = useCourseMaterialTypeApi();
  const { search: searchCourseStatuses, isLoading: isLoadingCourseStatuses } = useCourseStatusApi();
  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = usePaymentTypes();
  const { search: searchEvaluationStatuses, isLoading: isLoadingEvaluationStatuses } =
    useEvaluationStatusApi();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();

  const navigate = useNavigate();
  const location = useLocation();

  // Extract status from URL
  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Clear items when route variant changes (prevents showing stale data from previous page)
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      setItems([]); // Clear old data immediately
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  const roles = getRoles();

  // Status filtering functions using constants
  const getFiltersFromStatus = (status: string) => {

    switch (status.toLowerCase()) {
      case 'print':
        return {
          courseMaterialTypeId: CourseMaterialTypeConstants.PRINT,
          isDeleted: false,
        };
      case 'downloaded':
        return {
          courseMaterialTypeId: CourseMaterialTypeConstants.DOWNLOAD,
          isDeleted: false,
        };
      case 'sport':
        return {
          courseTypeId: CourseTypeConstants.DEDICATED_SPORTS_PERSON,
          isDeleted: false,
        };
      case 'hunt':
        return {
          courseTypeId: CourseTypeConstants.DEDICATED_HUNTER,
          isDeleted: false,
        };
      case 'huntsport':
        return {
          courseTypeId: CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
          isDeleted: false,
        };
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'print':
        return 'Printed';
      case 'downloaded':
        return 'Downloaded';
      case 'sport':
        return 'Sport';
      case 'hunt':
        return 'Hunt';
      case 'huntsport':
        return 'Hunt & Sport';
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Updated fetchItems function with status filtering
  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = getFiltersFromStatus(statusFromUrl);

      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Courses:', error);
      return 0;
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const previousFiltersRef = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  // Fetch items when dependencies change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);
    
    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef.current) {
      return;
    }
    
    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    // This handles the case where user navigates between routes and searchFilters is stale
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, {
      ...urlFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl]);

  // Load dropdown data - simplified since we use constants
  useEffect(() => {
    const loadDropdownData = async () => {
      // Set cached hook data first (these don't need async fetching)
      setCourseTypes(courseTypesData || []);
      setPaymentTypes(paymentTypesData || []);

      try {
        // Fetch all dropdown data
        const [
          courseMaterialTypesResponse,
          courseStatusesResponse,
          evaluationStatusesResponse,
        ] = await Promise.all([
          searchCourseMaterialTypes({ page: 1, pageSize: 9999, sortBy: 'DisplayPosition', sortDescending: false }),
          searchCourseStatuses({ page: 1, pageSize: 9999, sortBy: 'DisplayPosition', sortDescending: false }),
          searchEvaluationStatuses({
            page: 1,
            pageSize: 9999,
            sortBy: 'DisplayPosition',
            sortDescending: false,
          }),
        ]);

        setCourseMaterialTypes(courseMaterialTypesResponse?.data?.results || []);
        setCourseStatuses(courseStatusesResponse?.data?.results || []);
        setEvaluationStatuses(evaluationStatusesResponse?.data?.results || []);

        // Only set filters if there's a status from URL
        if (statusFromUrl) {
          const statusFilters = getFiltersFromStatus(statusFromUrl);
          setSearchFilters((prev: any) => ({
            ...prev,
            ...statusFilters,
          }));
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    loadDropdownData();
  }, [statusFromUrl, paymentTypesData, courseTypesData]);

  // Updated search handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);

    fetchItems(1, pageSize, {
      ...searchFilters,
    });
  };

  const handleMemberClick = (memberId: string) => {
    const course = items.find((item) => item.memberId === memberId);
    const idPassportNumber = course?.member?.idPassportNumber || '';

    // Set flag to prevent intermediate useEffect from running with stale URL
    isCommittingSearchRef.current = true;

    // Clear all filters and search only by ID/Passport.
    const newFilters = idPassportNumber ? { idPassportNumber } : {};
    // The useEffect watching location.search will trigger the fetch automatically
    commitSearch(newFilters, { page: 1, showAdvanced: false, replace: false });
  };

  const handleClearSearch = () => {
    clearSearchState();

    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: CourseDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Course:', error);
    }
  };

  // Email handlers from useEmailHandlers hook
  const { handleSendEmail, handleSendEmailSubmit, handleViewAllEmails, handleViewRecordEmails } = useEmailHandlers<CourseDTO>({
    setSelectedItem: setSelectedCourse,
    setDrawerTitle,
    setDrawerView,
    setIsDrawerOpen,
    sendEmail,
    fetchItems: async (page: number, size: number, filters: any) => {
      await fetchItems(page, size, filters);
    },
    currentPage,
    pageSize,
    searchFilters,
  });

  const handleRestore = async (course: CourseDTO) => {
    try {
      await restoreFromDeleted(course.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring course:', error);
    }
  };

  const handleOpenDrawer = async (
    course: CourseDTO,
    drawerView: CourseDrawerViews,
    drawerTitle: string,
    drawerSize: 'small' | 'large' = 'large',
    defaultStatusId?: string,
  ) => {
    setSelectedCourse(course);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    if (defaultStatusId) setDefaultStatusId(defaultStatusId);
    setIsDrawerOpen(true);
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedCourse({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImpersonateMemberLogin = async (dto: CourseDTO): Promise<void> => {
    if (!dto.member?.userID) {
      console.error('No member user ID found for impersonation');
      return;
    }

    try {
      // Open impersonation in new tab - ImpersonationRedirect will handle API call and redirect
      window.open(`/admin/impersonate/${dto.memberId}`, '_blank');
    } catch (error) {
      console.error('Error opening impersonation tab', error);
    }
  };

  // Updated export handler
  const handleExport = async () => {
    try {
      const statusFilters = statusFromUrl
        ? getFiltersFromStatus(statusFromUrl)
        : { isDeleted: false }; // For "All", only exclude deleted

      const response = await extract({
        filters: {
          ...searchFilters,
          ...statusFilters,
        },
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Courses:', error);
    }
  };

  const handleViewEvaluation = async (course: CourseDTO) => {
    try {
      const response = await getByCourseId(course.id ?? '');
      const responseData = response?.data as CourseEvaluationDTO | CourseEvaluationDTO[] | null | undefined;
      const evaluations = Array.isArray(responseData)
        ? responseData.filter((evaluation): evaluation is CourseEvaluationDTO => !!evaluation)
        : responseData
          ? [responseData]
          : [];

      setCourseEvaluation(evaluations);
      setDrawerView('evaluation');
      setDrawerTitle('View Evaluation');
      setIsDrawerOpen(true);
    } catch (error) {
      console.error('Error getting course evaluation', error);
    }
  };

  const handleDrawerClose = () => {
    setCourseEvaluation([]);
    setIsDrawerOpen(false);
    setSelectedCourse({});
    setDefaultStatusId(undefined);
    setDrawerSize('large');
  };

  const handleSelectRow = (id: string, isSelected: boolean) => {
    setSelectedCourseIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allIds = items.map(item => item.id).filter((id): id is string => !!id);
      setSelectedCourseIds(new Set(allIds));
    } else {
      setSelectedCourseIds(new Set());
    }
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedCourseIds)[0];
    const firstCourse = items.find(c => c.id === firstSelectedId) || {};
    const count = selectedCourseIds.size;
    handleOpenDrawer(
      firstCourse as CourseDTO,
      DrawerViewConstants.FEEDBACK,
      `Update Status (${count} course${count > 1 ? 's' : ''})`
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which course IDs to update
      const idsToUpdate = selectedCourseIds.size > 0
        ? Array.from(selectedCourseIds)
        : [selectedCourse.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        courseStatusId: data.courseStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
        member: data.member,
        courierTrackingReference: data.courierTrackingReference,
        timeMaterialShipped: data.timeMaterialShipped,
      };

      // Call the bulk update endpoint
      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedCourse({});
      setSelectedCourseIds(new Set()); // Clear selections
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendProofOfPaymentLink = async (course: CourseDTO) => {
    try {
      await sendProofOfPaymentLink(course.id ?? '');
    } catch (error) {
      console.error('Error sending proof of payment link:', error);
    }
  };

  const handleCompleteEftPayment = async (course: CourseDTO) => {
    try {
      await processEftPayment({ componentId: course.id, entityType: 'Course' });
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error completing EFT payment:', error);
    }
  };

  const handleDownloadOriginalCertificate = async (course: CourseDTO) => {
    try {
      const url = await generateDedicatedCertificate(course.courseTypeId ?? '', course.id ?? '');
      setViewingFile({ name: `Dedicated-Status-Certificate-${course.id}.pdf`, url });
    } catch (error) {
      console.error('Error generating original certificate:', error);
    }
  };

  const handleDownloadForfeitureDocument = async (course: CourseDTO) => {
    try {
      const url = await getForfeitureDocument(course.id ?? '');
      setViewingFile({ name: `Forfeiture-Document-${course.id}.pdf`, url });
    } catch (error) {
      console.error('Error generating forfeiture document:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={statusFromUrl ? `/admin/courses/status/${statusFromUrl}` : '/admin/courses'}
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Courses', href: '#' },
        ...(statusFromUrl
          ? [
            {
              label: getStatusDisplayName(statusFromUrl),
              href: '#',
            },
          ]
          : []),
      ]}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.Course.View"
    >
      <Card
        title={`Manage Courses ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedCourseIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedCourseIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkStatusUpdate();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Update Status
                    </button>
                  </div>
                )}
              </div>
            )}
            {roles?.includes('Admin') && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAdvanced(!showAdvanced);
                  }}
                >
                  {showAdvanced ? 'Basic Search' : 'Advanced Search'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleExport}>
                  Export
                </Button>
              </>
            )}

            {roles?.includes('User') && (
              <AddNewButton onClick={() => navigate('/admin/courses/create')} />
            )}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Member Search Fields - First Priority */}
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firstName: e.target.value,
                  })
                }
                placeholder="Search by First Name"
              />
              <FormField
                id="lastName"
                label="Search by Last Name"
                value={searchFilters.lastName || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    lastName: e.target.value,
                  })
                }
                placeholder="Search by Last Name"
              />
              <FormField
                id="idPassportNumber"
                label="Search by ID/Passport Number"
                value={searchFilters.idPassportNumber || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    idPassportNumber: e.target.value,
                  })
                }
                placeholder="Search by ID/Passport Number"
              />

              {/* Course Related Fields */}
              <FormField
                id="courseTypeId"
                label="Search by Course Type"
                value={searchFilters.courseTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    courseTypeId: e.target.value,
                  })
                }
                placeholder="Select Course Type"
                options={courseTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingCourseTypes}
              />
              <FormField
                id="courseStatusId"
                label="Search by Course Status"
                value={searchFilters.courseStatusId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    courseStatusId: e.target.value,
                  })
                }
                placeholder="Select Course Status"
                options={courseStatuses.map((status) => ({
                  value: status.id,
                  label: status.title || '',
                }))}
                isLoading={isLoadingCourseStatuses}
              />
              <FormField
                id="courseMaterialTypeId"
                label="Search by Material Type"
                value={searchFilters.courseMaterialTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    courseMaterialTypeId: e.target.value,
                  })
                }
                placeholder="Select Material Type"
                options={courseMaterialTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingCourseMaterialTypes}
              />
              <FormField
                id="evaluationStatusId"
                label="Search by Evaluation Status"
                value={searchFilters.evaluationStatusId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    evaluationStatusId: e.target.value,
                  })
                }
                placeholder="Select Evaluation Status"
                options={evaluationStatuses.map((status) => ({
                  value: status.id,
                  label: status.title || '',
                }))}
                isLoading={isLoadingEvaluationStatuses}
              />

              {/* Payment Related Fields */}
              <FormField
                id="paymentTypeId"
                label="Search by Payment Type"
                value={searchFilters.paymentTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    paymentTypeId: e.target.value,
                  })
                }
                placeholder="Select Payment Type"
                options={paymentTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingPaymentTypes}
              />
              <FormField
                id="paymentReference"
                label="Search by Payment Reference"
                value={searchFilters.paymentReference || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    paymentReference: e.target.value,
                  })
                }
                placeholder="Search by Payment Reference"
              />
              <FormField
                id="courierTrackingReference"
                label="Search by Courier Tracking Reference"
                value={searchFilters.courierTrackingReference || ''}
                type="text"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    courierTrackingReference: e.target.value,
                  })
                }
                placeholder="Search by Courier Tracking Reference"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex space-x-4 mb-4">
            {showAdvanced && (
              <>
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </>
            )}

            {!showAdvanced && roles?.includes('Admin') && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}
          </div>
        </form>

        <CourseTable
          data={areFiltersStale ? [] : items}
          handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
          handleViewEvaluation={handleViewEvaluation}
          handleEdit={(dto) => navigate(`/admin/courses/${dto.id}`)}
          handleDelete={handleDelete}
          handleSendEmail={handleSendEmail}
          handleViewAllEmails={handleViewAllEmails}
          handleViewRecordEmails={handleViewRecordEmails}
          handleRestore={handleRestore}
          handleApprove={(dto) =>
            handleOpenDrawer(
              dto,
              DrawerViewConstants.FEEDBACK,
              'Approve',
              'large',
              CourseStatusConstants.STATUS_APPROVED,
            )
          }
          handleShipped={(dto) =>
            handleOpenDrawer(
              dto,
              DrawerViewConstants.FEEDBACK,
              'Ship Material',
              'large',
              CourseStatusConstants.SHIPPED,
            )
          }
          handleChangeStatus={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Change Status')
          }
          handleImpersonateMemberLogin={handleImpersonateMemberLogin}
          handleAddNote={(dto) => handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Add Note')}
          handleViewNoteHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Notes History')
          }
          handleViewInvoiceHistory={(dto) =>
            handleOpenDrawer(dto, DrawerViewConstants.VIEW_INVOICE_HISTORY, 'Invoice History')
          }
          handleSendProofOfPaymentLink={handleSendProofOfPaymentLink}
          handleCompleteEftPayment={handleCompleteEftPayment}
          handleDownloadForfeitureDocument={handleDownloadForfeitureDocument}
          handleDownloadOriginalCertificate={handleDownloadOriginalCertificate}
          handleMemberClick={handleMemberClick}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
          {...(roles?.includes('Admin') ? {
            selectedIds: selectedCourseIds,
            onSelectRow: handleSelectRow,
            onSelectAll: handleSelectAll,
          } : {})}
        />

        <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} title={drawerTitle} size={drawerSize}>
          {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
            <MemberQuickDetails
              memberId={selectedCourse.member?.id ?? ''}
              onClose={handleDrawerClose}
            />
          )}
          {drawerView === DrawerViewConstants.EVALUATION && (
            <CourseEvaluation
              courseEvaluation={courseEvaluation}
              onClose={handleDrawerClose}
            />
          )}
          {drawerView === DrawerViewConstants.FEEDBACK && (
            <FeedbackForm
              componentId={ComponentConstants.DEDICATED_COURSES}
              recordId={selectedCourse.id ?? ''}
              componentStatuses={courseStatuses.map((status) => ({
                value: status.id,
                label: status.title,
              }))}
              statusFieldName={statusFieldName}
              defaultStatusId={defaultStatusId}
              initialData={selectedCourse}
              onSubmit={handleStatusSubmit}
              onCancel={handleDrawerClose}
              updateLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.ADD_NOTE && (
            <AddNote
              memberId={selectedCourse.member?.id || ''}
              componentId={ComponentConstants.DEDICATED_COURSES}
              recordId={selectedCourse.id ?? ''}
              onSubmit={handleAddNote}
              onCancel={handleDrawerClose}
              isLoading={isSubmitting}
            />
          )}
          {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && (
            <ViewNoteHistory memberId={selectedCourse.member?.id || ''} />
          )}
          {drawerView === DrawerViewConstants.VIEW_INVOICE_HISTORY && (
            <ViewInvoiceHistory memberId={selectedCourse.memberId ?? ''} />
          )}
          <EmailDrawerContent
            drawerView={drawerView}
            selectedItem={selectedCourse}
            componentId={ComponentConstants.DEDICATED_COURSES}
            getMemberId={(dto) => dto?.member?.id || ''}
            onEmailSubmit={handleSendEmailSubmit}
            onClose={handleDrawerClose}
            isSending={isSendingEmail}
          />
        </Drawer>
      </Card>

      {viewingFile && (
        <FileViewer
          sasUrl={viewingFile.url}
          fileName={viewingFile.name}
          onClose={() => setViewingFile(null)}
        />
      )}
    </AdminLayout>
  );
};

export default CourseList;